<template>
  <v-container fluid class="pa-6 pt-3">
      <v-card-text class="pa-4">
        <v-row align="center" dense>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="form.search"
              label="Search Referrer"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              hide-details="auto"
              @keyup.enter="getReferrals"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-autocomplete
              v-model="form.partner_id"
              :items="partners"
              item-text="name"
              item-value="id"
              label="Partner"
              outlined
              dense
              clearable
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-autocomplete
              v-model="form.program_id"
              :items="programItems"
              item-text="label"
              item-value="id"
              label="Programme"
              outlined
              dense
              clearable
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="displayDate"
                  label="Date Range"
                  placeholder="dd-mm-yyyy"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="resetDate"
                />
              </template>
              <v-date-picker v-model="selectedDates" range @input="onDateChange" />
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-btn
              color="accent"
              class="white--text"
              :loading="loading"
              @click="getReferrals"
            >
              Search
            </v-btn>

            <v-btn
              color="accent"
              class="white--text ml-2"
              :disabled="downloading || referrals.length === 0"
              :loading="downloading"
              @click="download"
            >
              Download
            </v-btn>

            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  color="accent"
                  class="white--text ml-2"
                  v-bind="attrs"
                  v-on="on"
                  @click="goToVisibility"
                >
                  <v-icon small>mdi-cog</v-icon>
                </v-btn>
              </template>
              <span>Visibility Settings</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-text>

    <v-card elevation="1" class="mx-3">
      <v-data-table
        :headers="headers"
        :items="referrals"
        :loading="loading"
        loading-text="Loading referrals…"
        fixed-header
        class="referral-table"
      >
        <template #[`item.full_name`]="{ item }">
          <span class="font-weight-medium">{{ item.full_name || "-" }}</span>
        </template>

        <template #[`item.program_names`]="{ item }">
          <div v-if="programLabels(item).length" class="programme-chips">
            <v-chip
              v-for="(name, index) in programLabels(item)"
              :key="index"
              x-small
              :outlined="!isSelectedProgram(item, index)"
              :text-color="isSelectedProgram(item, index) ? 'white' : undefined"
              color="primary"
              :title="
                isSelectedProgram(item, index) ? 'Selected programme' : undefined
              "
            >
              {{ name }}
            </v-chip>
          </div>
          <span v-else>-</span>
        </template>

        <template #[`item.city`]="{ item }">
          {{ item.city || "-" }}
        </template>

        <template #[`item.state`]="{ item }">
          {{ item.state || "-" }}
        </template>

        <template #[`item.referrer_usn_id`]="{ item }">
          {{ item.referrer_usn_id || "-" }}
        </template>

        <template #[`item.referrer_partner_name`]="{ item }">
          {{ item.referrer_partner_name || "-" }}
        </template>

        <template #[`item.referred_on`]="{ item }">
          {{ formatDateTime(item.referred_on) }}
        </template>

        <template #[`item.status`]="{ item }">
          <v-tooltip bottom :disabled="!item.remarks">
            <template #activator="{ on }">
              <v-chip
                small
                :color="statusColor(item.status)"
                text-color="white"
                v-on="on"
              >
                {{ statusLabel(item.status) }}
                <v-icon v-if="item.remarks" x-small right>
                  mdi-comment-text-outline
                </v-icon>
              </v-chip>
            </template>
            <span>{{ item.remarks }}</span>
          </v-tooltip>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn icon small title="Update status" @click="openStatusDialog(item)">
            <v-icon small>mdi-pencil-outline</v-icon>
          </v-btn>
        </template>

      </v-data-table>
    </v-card>

    <v-dialog v-model="statusDialog" width="520" persistent>
      <v-card class="secondary">
        <v-card-title class="primary white--text">Update Status</v-card-title>
        <v-card-text class="pt-5">
          <div v-if="statusForm.item" class="dialog-head mb-2">
            <span class="dialog-head__name">{{ statusForm.item.full_name }}</span>
            <span class="dialog-head__meta">
              Referred by {{ statusForm.item.referrer_name }}
              ({{ statusForm.item.referrer_usn_id || "-" }})
            </span>
          </div>

          <v-select
            v-model="statusForm.status"
            :items="statusItems"
            item-text="label"
            item-value="value"
            label="Status"
            outlined
            dense
            hide-details="auto"
            class="mb-4"
          />

          <v-select
            v-model="statusForm.selected_program_code"
            :items="programCodeItems"
            item-text="label"
            item-value="value"
            label="Programme Code"
            :hint="
              programCodeItems.length
                ? 'Choose the programme this referral is being taken forward with'
                : 'This referral has no programme codes'
            "
            persistent-hint
            :disabled="!programCodeItems.length"
            outlined
            dense
            clearable
            class="mb-4"
          />

          <v-textarea
            v-model="statusForm.remarks"
            label="Remarks (optional)"
            placeholder="Follow-up notes, call outcome, next steps…"
            outlined
            dense
            rows="3"
            counter="500"
            :rules="[remarksRule]"
            hide-details="auto"
          />
        </v-card-text>
        <v-card-actions class="justify-end pb-4 px-6">
          <v-btn text :disabled="saving" @click="statusDialog = false">Cancel</v-btn>
          <v-btn
            color="accent"
            class="white--text"
            :loading="saving"
            :disabled="!statusForm.status || remarksRule(statusForm.remarks) !== true"
            @click="saveStatus"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { referralHeaders } from "@/constants/headers";

const STATUS_LABELS = {
  New: "New",
  Contacted: "Contacted",
  Enrolled: "Enrolled",
  Rejected: "Rejected",
};

const STATUS_COLORS = {
  New: "blue-grey",
  Contacted: "amber darken-2",
  Enrolled: "success",
  Rejected: "error",
};


const EXCLUDED_PROGRAMMES = [/b\.?\s*ed\b/i, /\bold\b/i];


const formatIsoDate = (value) => {
  if (!value) return "";
  const [year, month, day] = String(value).split("-");
  return day && month && year ? `${day}-${month}-${year}` : value;
};

export default {
  data() {
    return {
      headers: referralHeaders,
      statuses: ["New", "Contacted", "Enrolled", "Rejected"],

      referrals: [],
      loading: false,
      downloading: false,

      form: {
        search: null,
        partner_id: null,
        program_id: null,
        status: null,
        from_date: null,
        to_date: null,
      },
      selectedDates: [],
      dateMenu: false,

      partners: [],
      programs: [],

      statusDialog: false,
      saving: false,
      statusForm: {
        item: null,
        status: null,
        remarks: "",
        selected_program_code: null,
      },
    };
  },

  computed: {
    programItems() {
      return this.programs
        .map((program) => ({
          id: program.id,
          label: this.programLabel(program),
        }))
        .filter(({ label }) => !EXCLUDED_PROGRAMMES.some((re) => re.test(label)));
    },

    statusItems() {
      return Object.keys(STATUS_LABELS).map((status) => ({
        value: status,
        label: STATUS_LABELS[status],
      }));
    },

    programCodeItems() {
      const item = this.statusForm.item;
      if (!item) return [];

      const names = item.program_names || [];
      return (item.program_codes || []).map((code, index) => ({
        value: code,
        label: names[index],
      }));
    },

    statusCounts() {
      return this.referrals.reduce((acc, item) => {
        acc[item.status] = (acc[item.status] || 0) + 1;
        return acc;
      }, {});
    },

    
    displayDate() {
      const [from, to] = this.selectedDates;
      if (!from) return "";
      return to && to !== from
        ? `${formatIsoDate(from)} to ${formatIsoDate(to)}`
        : formatIsoDate(from);
    },
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Referrals" }]);
    await Promise.all([this.getPartners(), this.getPrograms()]);
    await this.getReferrals();
  },

  methods: {
    goToVisibility() {
      this.$router.push({ name: "Referral Visibility" });
    },

    queryParams() {
      const params = new URLSearchParams();
      Object.entries(this.form).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      return params.toString();
    },

    
    async getReferrals() {
      this.loading = true;
      try {
        const query = this.queryParams();
        const res = await this.$axios.get(`/referrals${query ? `?${query}` : ""}`);
        this.referrals = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load referrals");
      } finally {
        this.loading = false;
      }
    },

    async getPartners() {
      try {
        const res = await this.$axios.get("partners");
        this.partners = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      }
    },

    async getPrograms() {
      try {
        const res = await this.$axios.get("/programs?active=true");
        this.programs = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load programmes");
      }
    },

    onDateChange(dates) {
      const [from, to] = [...(dates || [])].sort();
      this.form.from_date = from || null;
      this.form.to_date = to || from || null;
      if (from && to) this.dateMenu = false;
    },

    resetDate() {
      this.selectedDates = [];
      this.form.from_date = null;
      this.form.to_date = null;
    },

    openStatusDialog(item) {
      const codes = item.program_codes || [];

      this.statusForm = {
        item,
        status: item.status || "New",
        remarks: item.remarks || "",
        // Nothing to choose when the referral names a single programme.
        selected_program_code:
          item.selected_program_code || (codes.length === 1 ? codes[0] : null),
      };
      this.statusDialog = true;
    },

    async saveStatus() {
      const { item, status, remarks, selected_program_code } = this.statusForm;
      if (!item) return;

      this.saving = true;
      try {
        await this.$axios.put(`/referrals/${item.id}/status`, {
          student_referral: {
            status,
            remarks,
            selected_program_code: selected_program_code || null,
          },
        });

        this.$set(item, "status", status);
        this.$set(item, "remarks", remarks);
        this.$set(item, "selected_program_code", selected_program_code || null);
        this.$store.commit("showSnackbar", {
          message: "Status updated",
          color: "success",
        });
        this.statusDialog = false;
      } catch (err) {
        this.notifyError(err, "Unable to update the status");
      } finally {
        this.saving = false;
      }
    },

    async download() {
      this.downloading = true;
      try {
        const query = this.queryParams();
        const res = await this.$axios.get(
          `/download_referrals${query ? `?${query}` : ""}`,
          { responseType: "blob" }
        );
        const url = window.URL.createObjectURL(
          new Blob([res.data], { type: "text/csv" })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "referrals.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.notifyError(err, "Unable to download referrals");
      } finally {
        this.downloading = false;
      }
    },

    statusLabel(status) {
      return STATUS_LABELS[status] || status || "-";
    },

    statusColor(status) {
      return STATUS_COLORS[status] || "grey";
    },

    remarksRule(value) {
      return (value || "").length <= 500 || "Max 500 characters";
    },

    programLabels(item) {
      const names = item.program_names || [];
      return names.length ? names : item.program_codes || [];
    },

    isSelectedProgram(item, index) {
      const code = (item.program_codes || [])[index];
      return Boolean(code) && code === item.selected_program_code;
    },

    programLabel(program) {
      const name = program.degree?.short_name || "";
      return (
        `${name}${program.specialization ? " - " + program.specialization : ""}`.trim() ||
        program.code
      );
    },

    formatDateTime(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    notifyError(err, fallback) {
      const data = err?.response?.data;
      const changesetError = data?.errors
        ? Object.entries(data.errors)
            .map(([field, msgs]) => `${field} ${[].concat(msgs).join(", ")}`)
            .join("; ")
        : null;

      this.$store.commit("showSnackbar", {
        message: data?.message || changesetError || fallback,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.filter-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.summary-card {
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.summary-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.summary-value {
  color: #0f172a;
  font-size: 26px;
  font-weight: 700;
  margin-top: 4px;
}

.referral-table {
  border-top: 1px solid #eeeeee;
}

.v-data-table >>> th {
  font-weight: 700 !important;
  white-space: nowrap;
}

.v-data-table >>> td {
  white-space: nowrap;
}

.programme-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 320px;
  white-space: normal;
  padding: 4px 0;
}

.table-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 44px 20px;
}

.table-empty__title {
  margin: 12px 0 4px;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
}

.table-empty__text {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

@media (max-width: 960px) {
  .summary-value {
    font-size: 22px;
  }
}
</style>
